import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Copy, Link2, Loader2, RefreshCw, ShieldOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = { not_enabled: 'Not Enabled', active: 'Active', expired: 'Expired', revoked: 'Revoked' };
const TONES = {
  not_enabled: 'bg-slate-100 text-slate-600', active: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-amber-100 text-amber-800', revoked: 'bg-rose-100 text-rose-700',
};
const fmt = (value) => value ? new Date(value).toLocaleString() : '—';

export const ShareReportDialog = ({ reportId }) => {
  const [open, setOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState('');
  const [noExpiry, setNoExpiry] = useState(true);
  const [expiry, setExpiry] = useState('');
  const shareUrl = useMemo(() => share?.share_path ? `${window.location.origin}${share.share_path}` : '', [share]);

  const apply = (data) => {
    setShare(data);
    setNoExpiry(!data.expires_at);
    setExpiry(data.expires_at ? data.expires_at.slice(0, 10) : '');
  };
  const load = async () => {
    try { apply((await axios.get(`${API}/development-reports/${reportId}/share`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load sharing status'); }
  };
  useEffect(() => { if (open) load(); }, [open, reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (kind, request) => {
    setBusy(kind);
    try { apply((await request()).data); toast.success(kind === 'revoke' ? 'Secure link revoked' : 'Sharing updated'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Sharing update failed'); }
    finally { setBusy(''); }
  };
  const body = { expires_on: noExpiry ? null : (expiry || null) };
  const copy = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Secure link copied'); }
    catch (_) { toast.error('Could not copy the link'); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-share-report-dialog"><Link2 className="mr-1.5 h-4 w-4" /> Share / Review Link</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="share-report-dialog">
        <DialogHeader><DialogTitle>Secure Share / Review Link</DialogTitle></DialogHeader>
        {!share ? <div className="py-8 text-center text-sm text-slate-400" data-testid="share-status-loading">Loading…</div> : (
          <div className="space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <span className="text-sm text-slate-500">Status</span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${TONES[share.status]}`} data-testid="share-link-status">{LABELS[share.status]}</span>
            </div>
            {shareUrl && (
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500" htmlFor="share-link-value">Secure URL</label>
                <div className="flex gap-2">
                  <Input id="share-link-value" value={shareUrl} readOnly data-testid="share-link-value" />
                  <Button size="icon" variant="outline" onClick={copy} title="Copy secure link" data-testid="copy-share-link"><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Meta label="Created" value={fmt(share.created_at)} testId="share-created-at" />
              <Meta label="Last Accessed" value={fmt(share.last_accessed_at)} testId="share-last-accessed" />
              <Meta label="Expiration" value={share.expires_at ? fmt(share.expires_at) : 'No Expiration'} testId="share-expiration" />
              <Meta label="View Count" value={String(share.access_count || 0)} testId="share-view-count" />
            </div>
            <div className="space-y-2 border-t border-slate-100 pt-4">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={noExpiry} onChange={(e) => setNoExpiry(e.target.checked)} data-testid="share-no-expiration" /> No Expiration
              </label>
              {!noExpiry && <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="share-expiration-date" />}
            </div>
            <div className="flex flex-wrap gap-2">
              {share.status === 'not_enabled' || share.status === 'revoked' ? (
                <Button onClick={() => run('generate', () => axios.post(`${API}/development-reports/${reportId}/share`, body))} disabled={!!busy} data-testid="generate-share-link">
                  {busy === 'generate' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Link2 className="mr-1.5 h-4 w-4" />} Generate Secure Link
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => run('expiration', () => axios.put(`${API}/development-reports/${reportId}/share/expiration`, body))} disabled={!!busy || (!noExpiry && !expiry)} data-testid="change-share-expiration">Change Expiration</Button>
                  <Button variant="outline" onClick={() => run('regenerate', () => axios.post(`${API}/development-reports/${reportId}/share/regenerate`, body))} disabled={!!busy} data-testid="regenerate-share-link">
                    <RefreshCw className="mr-1.5 h-4 w-4" /> Regenerate
                  </Button>
                  <Button variant="outline" className="text-rose-600" onClick={() => run('revoke', () => axios.post(`${API}/development-reports/${reportId}/share/revoke`))} disabled={!!busy} data-testid="revoke-share-link">
                    <ShieldOff className="mr-1.5 h-4 w-4" /> Revoke
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Meta = ({ label, value, testId }) => <div data-testid={testId}><div className="text-slate-400">{label}</div><div className="mt-0.5 font-medium text-slate-700">{value}</div></div>;

export default ShareReportDialog;